import { useLegacyDialogProps } from 'components/DialogV3/DialogManagerContext';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    IconButton,
    InputAdornment,
    Typography,
} from '@mui/material';
import apiMcu from 'api/mcu';
import { IconClose } from 'assets/icons';
import TextFieldVMP from 'components/common/TextField/TextFieldVMP';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from 'theme';
import { setCloseDialogCC, toastError, toastSuccess } from 'utils/helpers';
import DialogBaseCC from './DialogBaseCC';
import { DIALOG_ID } from './constant';

function validate(key, value, t) {
    const regexInteger = /^[1-9]\d*$/;

    switch (key) {
        case 'name':
            if (value?.length === 0) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameRole.require',
                };
            }
            if (value?.length > 255) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameRole.max-length',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        case 'code':
            if (!regexInteger.test(value)) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameCode.positiveNumber',
                };
            }
            if (value.length < 6) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameCode.min',
                };
            }
            if (value.length > 10) {
                return {
                    value: true,
                    helperText: 'mcu.helperText.validate.nameCode.limit',
                };
            }
            return {
                value: false,
                helperText: '',
            };
        default:
            break;
    }
}

const DialogCreateMCURoom = () => {
    const propsDialog = useLegacyDialogProps();
    const { title, titleButtonAction, titleButtonCancel, data, agreeAction } = propsDialog;
    const { open } = useSelector((state) => state.dialog);
    const { t } = useTranslation();
    const [errorWhenRoomIsProgressed, setErrorWhenRoomIsProgressed] = useState(false);

    const [newItem, setNewItem] = useState({
        name: '',
        code: '',
    });
    const handleValidateMCURoom = (data, t) => {
        const error = {
            name: { value: false, helperText: '' },
            code: { value: false, helperText: '' },
        };

        Object.keys(error).forEach((key) => (error[key] = validate(key, data[key], t)));
        return error;
    };
    const [errorValid, setErrorValid] = useState(() => handleValidateMCURoom(newItem, t));
    const [clickSubmit, setClickSubmit] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const apiCreateMCURoom = (code, name) => {
        return apiMcu.createRoom(code, name);
    };

    const apiUpdateRoom = (id, code, name) => {
        return apiMcu.updateRoom(id, code, name);
    }

    const handleCreateMCURoom = (newItem) => {
        if (title !== t('mcu.dialog.title.editRoom')) {
            apiCreateMCURoom(newItem.code.trim(), newItem.name.trim())
                .then((result) => {
                    toastSuccess(t('mcu.toast.createRoomSuccessfully'))
                    setTimeout(() => {
                        agreeAction();
                    }, 100)
                })
                .catch((error) => {
                    if(error?.response?.data?.code === "ROOM_NUMBER_EXISTS"){
                        toastError(t('mcu.toast.roomCodeIsExisted'))
                    } else {
                        toastError(t('mcu.toast.error'))
                    }
                })
                .finally(() => {
                    setIsUpdating(false)
                    handleCloseDialog();
                });
        } else {
            apiUpdateRoom(newItem.id, newItem.code.trim(), newItem.name.trim())
                .then((result) => {
                    toastSuccess(t('mcu.toast.updateRoomSuccessfully'))
                    setTimeout(() => {
                        agreeAction();
                    }, 100)
                })
                .catch(error => {
                    if(error?.response?.data?.code === "ROOM_NUMBER_EXISTS"){
                        toastError(t('mcu.toast.roomCodeIsExisted'))
                        setIsUpdating(false)
                        handleCloseDialog();
                    }else if(error?.response?.data?.code === "ROOM_IN_PROGRESS"){
                        toastError(t('mcu.toast.errorUpdateRoom'))
                        setErrorWhenRoomIsProgressed(true);
                        setIsUpdating(false);
                    } else {
                        toastError(t('mcu.toast.error'))
                        setIsUpdating(false)
                        handleCloseDialog();
                    }
                })
                .finally(() => {
                    setIsUpdating(false)
                    handleCloseDialog();
                });;
        }
    };

    const handleCloseDialog = () => {
        setCloseDialogCC();
        setNewItem({
            name: '',
            code: '',
        });
    };  

    const handleAction = () => {
        setClickSubmit(true);
        setIsUpdating(true);
        if (errorValid['name']?.value) {
            setIsUpdating(false);
        } else if (errorValid['code']?.value) {
            setIsUpdating(false);
        } else {
            handleCreateMCURoom(newItem);
        }
    };

    useEffect(() => {
        setErrorValid((_) => handleValidateMCURoom(newItem, t));
    // eslint-disable-next-line
    }, [newItem]);

    useEffect(() => {
        if (data) {
            setNewItem({
                name: data?.name,
                code: data?.code,
                id: data?.id
            });
        }
    }, [data]);

    useEffect(() => {
        if (!open) {
            setNewItem({
                name: '',
                code: '',
            });
            setErrorValid({
                name: { value: false, helperText: '' },
                code: { value: false, helperText: '' },
            });
            setClickSubmit(false);
            setErrorWhenRoomIsProgressed(false);
        }
    }, [open]);

    return (
        <DialogBaseCC id={DIALOG_ID.CREATE_MCU_ROOM} title={title}>
            <DialogContent sx={{ minWidth: '500px !important' }}>
                <DialogContentText>
                    <Box>
                        {
                            errorWhenRoomIsProgressed &&
                            <Typography sx={{ color: '#CB002B' }}>
                                This meeting room is currently in progress, so you cannot edit it at this time.
                            </Typography>
                        }
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.content.roomCode')}<span style={{ color: color.main }}>*</span>
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterRoomCode')}
                                autoFocus
                                error={clickSubmit && errorValid['code']?.value}
                                helperText={clickSubmit && t(errorValid['code']?.helperText)}
                                value={newItem.code}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        code: e.target.value.trim(),
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.code !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            code: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        <Box sx={{ marginTop: { xs: '0.5rem', xl: '1rem' } }}>
                            <Typography sx={{ color: color.black }}>
                                {t('mcu.dialog.content.roomName')}<span style={{ color: color.main }}>*</span>
                            </Typography>
                            <TextFieldVMP
                                placeholder={t('mcu.dialog.placeholder.enterRoomName')}
                                error={clickSubmit && errorValid['name']?.value}
                                helperText={clickSubmit && t(errorValid['name']?.helperText)}
                                value={newItem.name}
                                onChange={(e) => {
                                    setNewItem({
                                        ...newItem,
                                        name: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {newItem.name !== '' ? (
                                                <IconButton
                                                    tabIndex={-1}
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setNewItem({
                                                            ...newItem,
                                                            name: '',
                                                        });
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <IconClose />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={() => {
                        handleCloseDialog();
                    }}
                >
                    {titleButtonCancel}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isUpdating}
                    onClick={() => {
                        handleAction();
                    }}
                    disabled={errorWhenRoomIsProgressed || newItem?.name?.length === 0 || newItem?.code?.length === 0}
                >
                    {titleButtonAction}
                </LoadingButton>
            </DialogActions>
        </DialogBaseCC>
    );
};

export default DialogCreateMCURoom;
